import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FileBarChart,
    Briefcase,
    Eye,
    Gavel,
    ShieldAlert,
    UserSquare2,
    ChevronRight,
    FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/card';

export const ReportsLanding = () => {
    const navigate = useNavigate();

    const reportModules = [
        {
            title: 'Resumen Ejecutivo',
            description: 'Visión general de alto nivel, riesgos y acciones clave.',
            icon: <FileBarChart className="h-6 w-6 text-blue-600" />,
            path: '/reports/executive',
            color: 'bg-blue-50 border-blue-100'
        },
        {
            title: 'Portafolio de Marcas',
            description: 'Inventario detallado, estatus, clases y vigencias.',
            icon: <Briefcase className="h-6 w-6 text-indigo-600" />,
            path: '/reports/portfolio',
            color: 'bg-indigo-50 border-indigo-100'
        },
        {
            title: 'Monitoreo & Gaceta',
            description: 'Alertas de vigilancia, similitudes y análisis de riesgo.',
            icon: <Eye className="h-6 w-6 text-purple-600" />,
            path: '/reports/monitoring',
            color: 'bg-purple-50 border-purple-100'
        },
        {
            title: 'Procedimientos & Litigios',
            description: 'Seguimiento legal, contestaciones y vencimientos.',
            icon: <Gavel className="h-6 w-6 text-amber-600" />,
            path: '/reports/procedures',
            color: 'bg-amber-50 border-amber-100'
        },
        {
            title: 'Brand Protection',
            description: 'Infracciones detectadas en marketplaces y redes sociales.',
            icon: <ShieldAlert className="h-6 w-6 text-red-600" />,
            path: '/reports/brand-protection',
            color: 'bg-red-50 border-red-100'
        },
        {
            title: 'Reporte por Cliente',
            description: 'Generador personalizado PDF para entrega a clientes.',
            icon: <UserSquare2 className="h-6 w-6 text-green-600" />,
            path: '/reports/client',
            color: 'bg-green-50 border-green-100'
        }
    ];

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-8 text-white shadow-lg relative overflow-hidden">
                <div className="relative z-10 max-w-2xl">
                    <h1 className="text-3xl font-bold mb-3">Centro de Reportes 4.0</h1>
                    <p className="text-gray-300 mb-6 text-lg">
                        Genera inteligencia de negocio a partir de tu portafolio marcario.
                        Analiza riesgos, monitoriza infracciones y exporta resultados oficiales.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Button
                            onClick={() => navigate('/reports/executive')}
                            className="bg-white text-gray-900 hover:bg-gray-100 border-none font-semibold"
                        >
                            <FileBarChart className="mr-2 h-4 w-4" />
                            Ver Resumen Ejecutivo
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/reports/client')}
                            className="bg-gray-800 text-white border-gray-600 hover:bg-gray-700 hover:text-white"
                        >
                            <FileText className="mr-2 h-4 w-4" />
                            Generar PDF Cliente
                        </Button>
                    </div>
                </div>

                {/* Abstract Background Decoration */}
                <div className="absolute right-0 top-0 h-full w-1/3 opacity-10 pointer-events-none">
                    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#FFFFFF" d="M44.7,-76.4C58.9,-69.2,71.8,-59.1,81.6,-46.6C91.4,-34.1,98.1,-19.2,95.8,-4.9C93.5,9.4,82.2,23.1,71.4,35.1C60.6,47.1,50.3,57.4,38.3,64.3C26.3,71.2,12.6,74.7,-0.4,75.4C-13.4,76.1,-26.1,74,-37.8,67.8C-49.5,61.6,-60.2,51.3,-68.8,39.2C-77.4,27.1,-83.9,13.2,-82.7,0.1C-81.5,-13,-72.6,-25.3,-62.7,-35.1C-52.8,-44.9,-41.9,-52.2,-30.5,-61.2C-19.1,-70.2,-7.2,-80.9,3.3,-86.6C13.8,-92.3,27.6,-93,44.7,-76.4Z" transform="translate(100 100)" />
                    </svg>
                </div>
            </div>

            {/* Modules Grid */}
            <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    Módulos Disponibles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {reportModules.map((module, idx) => (
                        <Card
                            key={idx}
                            className="group hover:shadow-md transition-all duration-200 cursor-pointer border-gray-200 hover:border-gray-300"
                            onClick={() => navigate(module.path)}
                        >
                            <CardHeader className="pb-3">
                                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${module.color}`}>
                                    {module.icon}
                                </div>
                                <CardTitle className="text-lg group-hover:text-indigo-600 transition-colors flex items-center justify-between">
                                    {module.title}
                                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-all font-bold" />
                                </CardTitle>
                                <CardDescription className="text-sm text-gray-500 line-clamp-2">
                                    {module.description}
                                </CardDescription>
                            </CardHeader>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Helper Footer */}
            <div className="text-center text-xs text-gray-400 mt-12 border-t border-gray-100 pt-6">
                Sistema de Reportes v4.0 • Mock Data Mode (Sin conexión a backend real)
            </div>
        </div>
    );
};
